import { useMemo } from 'react'
import { useGetList } from 'react-admin'
import { getRole } from '../common/roles'

const useEventsInfo = (centerId, identity, yearId = null) => {
  const isAdmin = useMemo(() =>
    identity?.role?.grade === getRole('admin'),
    [identity?.role?.grade]
  )
  
  const isPubeur = useMemo(() =>
    identity?.role?.grade < getRole('responsable'),
    [identity?.role?.grade]
  )
  
  const currentYearId = yearId ?? process.env.REACT_APP_CURRENT_YEAR_ID

  // --- 1. Fetch events with embedded event_pubeurs if pubeur
  const eventFilter = useMemo(() => {
    if (isPubeur) {
      // pubeurs should not see events MISSIONS LYCEE FORUM OR INTERVENTION NOT DISPLAYED YET
      return { 'locked@neq': true, 'event_pubeurs.profile_id': identity?.id }
    }
    
    if (!isAdmin) {
      return { center_id: centerId ?? identity?.center?.id }
    }

    return {}
  }, [isPubeur, identity?.id, isAdmin, centerId])

  const { data: rawEvents = [], isLoading: eventsLoading } = useGetList('events', {
    pagination: { page: 1, perPage: 9999 },
    sort: { field: 'date', order: 'DESC' },
    filter: eventFilter,
    meta: {
      columns: ['id', 'name', 'date', 'end_at', 'type_id', 'center_id', 'qualifications_ids', 'current_year_id', 'missionStartHidden', 'missionStartAff', 'event_pubeurs(id,profile_id)']
    }
  })

  // --- 2. Fetch qualifications using types_qualifications_view to get the type name
  const { data: qualifications = [], isLoading: qualificationsLoading } = useGetList('types_qualifications_view', {
    pagination: { page: 1, perPage: 9999 },
    sort: { field: 'name', order: 'ASC' },
  })

  // --- 3. Create a lookup map for qualifications
  const qualificationsMap = useMemo(() => {
    const map = {}
    qualifications.forEach(q => {
      map[q.id] = q
    })
    return map
  }, [qualifications])

  // --- 4. Filter and transform events
  const isDefaultYear = !yearId || yearId === process.env.REACT_APP_CURRENT_YEAR_ID

  const enhancedEvents = useMemo(() => {
    const currentYear = new Date().getFullYear()
    const july31Cutoff = new Date(`${currentYear}-07-31T23:59:59.999Z`)

    // Filter events: current year OR end_at after July 31st
    const relevantEventsByYear = rawEvents.filter(event => {
      // Include events that belong to selected year
      if (event.current_year_id === currentYearId) {
        return true
      }

      // Include events that end after July 31st — only for the default/current year
      // (summer overlap). When viewing a past year, only show that year's events.
      if (isDefaultYear && event.end_at && new Date(event.end_at) > july31Cutoff) {
        return true
      }

      return false
    })

    const now = new Date()

    const relevantEvents = isPubeur
      ? relevantEventsByYear.filter(event => {
          if ( ! event.event_pubeurs?.some(p => p.profile_id === identity.id) ) return false
          
          // events of certain types have MissionStartAff
          // and should have missionStartHidden to be able to add pubeurs
          // in that case:
          // don't display events not supposed to be visible
          if (
            event.missionStartAff &&
            (!event.missionStartHidden || new Date(event.missionStartHidden) > now)
          ) {
            return false
          }

          if ( event.end_at && new Date(event.end_at) <= now ) return false
          
          return true
        })
      : relevantEventsByYear

    return relevantEvents
      .map(event => {
        let startDate = null
        if (event.date) {
          const [y, m, d] = event.date.split('T')[0].split('-')
          startDate = `${d}/${m}/${y}`
        }

        const qualificationInfo = event.qualifications_ids
          ? event.qualifications_ids.map(id => qualificationsMap[id]).filter(Boolean)
          : []

        return {
          ...event,
          startDate,
          qualificationInfo
        }
      })
      .sort((a, b) => {
        if (!a.date) return 1
        if (!b.date) return -1
        return new Date(b.date) - new Date(a.date)
      })
  }, [rawEvents, qualificationsMap, isPubeur, currentYearId, isDefaultYear, identity?.id])

  // --- 5. Return the result
  return useMemo(() => ({
    eventsList: enhancedEvents,
    qualifications,
    yearId: currentYearId,
    isLoading: eventsLoading || qualificationsLoading
  }), [enhancedEvents, qualifications, currentYearId, eventsLoading, qualificationsLoading])
}

export default useEventsInfo